import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  Send, 
  Clock, 
  DollarSign, 
  FileWarning,
  Settings,
  Bell,
  ExternalLink,
  CheckCircle,
  XCircle,
  RefreshCw
} from "lucide-react";
import axios from "axios";

const Inkasso = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [cases, setCases] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    days_before_reminder: 14,
    days_before_inkasso: 30,
    reminder_fee: 60.0,
    interest_rate: 8.0,
    auto_send_reminders: true,
    auto_send_to_inkasso: false
  });

  const fetchData = useCallback(async () => {
    try {
      const [casesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/inkasso/cases`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inkasso/settings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCases(casesRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      console.error("Error fetching inkasso data:", error);
      toast.error("Kunde inte hämta inkassodata");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendReminder = async (caseId) => {
    try {
      const response = await axios.post(`${API}/inkasso/cases/${caseId}/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Kunde inte skicka påminnelse");
    }
  };

  const handleSendToInkasso = async (caseId) => {
    if (!window.confirm("Är du säker på att du vill skicka detta ärende till inkasso?")) return;
    
    try {
      const response = await axios.post(`${API}/inkasso/cases/${caseId}/send-to-inkasso`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Kunde inte skicka till inkasso");
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/inkasso/settings`, settingsForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Inställningar sparade");
      setSettingsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Kunde inte spara inställningar");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="border-amber-300 text-amber-700"><Clock size={12} className="mr-1" />Väntar</Badge>;
      case 'sent_to_inkasso':
        return <Badge className="bg-red-100 text-red-700"><ExternalLink size={12} className="mr-1" />Hos inkasso</Badge>;
      case 'paid':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" />Betald</Badge>;
      case 'written_off':
        return <Badge className="bg-slate-100 text-slate-700"><XCircle size={12} className="mr-1" />Avskriven</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const totalOverdue = cases.reduce((sum, c) => sum + c.original_amount, 0);
  const totalWithFees = cases.reduce((sum, c) => sum + c.total_amount, 0);
  const pendingCases = cases.filter(c => c.status === 'pending').length;
  const inkassoCases = cases.filter(c => c.status === 'sent_to_inkasso').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('inkasso.title')}</h1>
          <p className="text-slate-600">{t('inkasso.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={() => setSettingsDialogOpen(true)}>
          <Settings size={16} className="mr-2" />
          {t('inkasso.settings')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{cases.length}</p>
                <p className="text-xs text-slate-500">{t('inkasso.overdueInvoices')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{pendingCases}</p>
                <p className="text-xs text-slate-500">{t('inkasso.waitingForPayment')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <ExternalLink className="text-indigo-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-600">{inkassoCases}</p>
                <p className="text-xs text-slate-500">{t('inkasso.atCollections')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <DollarSign className="text-slate-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold">{formatCurrency(totalWithFees)}</p>
                <p className="text-xs text-slate-500">{t('inkasso.totalOutstanding')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <Bell className="text-amber-600" size={20} />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">
                {t('inkasso.reminderFee')}: {formatCurrency(settings?.reminder_fee || 60)} | 
                {t('inkasso.interestRate')}: {settings?.interest_rate || 8}% |
                {t('inkasso.autoReminders')}: {settings?.auto_send_reminders ? t('common.yes') : t('common.no')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cases Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('inkasso.overdueInvoices')}</CardTitle>
        </CardHeader>
        <CardContent>
          {cases.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle className="mx-auto mb-3 text-green-300" size={48} />
              <p>{t('common.noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t('invoices.invoiceNumber')}</th>
                    <th className="text-left p-3 font-medium">{t('invoices.customer')}</th>
                    <th className="text-center p-3 font-medium">{t('inkasso.daysOverdue')}</th>
                    <th className="text-right p-3 font-medium">{t('inkasso.originalAmount')}</th>
                    <th className="text-right p-3 font-medium">{t('inkasso.fees')}</th>
                    <th className="text-right p-3 font-medium">{t('common.total')}</th>
                    <th className="text-center p-3 font-medium">{t('common.status')}</th>
                    <th className="text-center p-3 font-medium">{t('inkasso.remindersSent')}</th>
                    <th className="text-right p-3 font-medium">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((inkassoCase) => (
                    <tr key={inkassoCase.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-mono text-indigo-600">{inkassoCase.invoice_number}</td>
                      <td className="p-3">{inkassoCase.customer_name}</td>
                      <td className="p-3 text-center">
                        <Badge className={`${inkassoCase.days_overdue > 30 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {inkassoCase.days_overdue} dagar
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-mono">{formatCurrency(inkassoCase.original_amount)}</td>
                      <td className="p-3 text-right text-xs text-slate-500">
                        <div>Avgift: {formatCurrency(inkassoCase.reminder_fee)}</div>
                        <div>Ränta: {formatCurrency(inkassoCase.interest_amount)}</div>
                      </td>
                      <td className="p-3 text-right font-mono font-bold text-red-600">
                        {formatCurrency(inkassoCase.total_amount)}
                      </td>
                      <td className="p-3 text-center">{getStatusBadge(inkassoCase.status)}</td>
                      <td className="p-3 text-center">
                        <Badge variant="outline">{inkassoCase.reminders_sent} st</Badge>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          {inkassoCase.status === 'pending' && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleSendReminder(inkassoCase.id)}
                                title="Skicka påminnelse"
                              >
                                <Bell size={14} />
                              </Button>
                              {inkassoCase.days_overdue >= (settings?.days_before_inkasso || 30) && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-red-600 border-red-300"
                                  onClick={() => handleSendToInkasso(inkassoCase.id)}
                                  title="Skicka till inkasso"
                                >
                                  <ExternalLink size={14} />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('inkasso.settingsTitle')}</DialogTitle>
            <DialogDescription>
              {t('inkasso.subtitle')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('inkasso.daysBeforeReminder')}</Label>
                <Input 
                  type="number"
                  value={settingsForm.days_before_reminder}
                  onChange={(e) => setSettingsForm({...settingsForm, days_before_reminder: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('inkasso.daysBeforeCollections')}</Label>
                <Input 
                  type="number"
                  value={settingsForm.days_before_inkasso}
                  onChange={(e) => setSettingsForm({...settingsForm, days_before_inkasso: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('inkasso.reminderFee')} (SEK)</Label>
                <Input 
                  type="number"
                  value={settingsForm.reminder_fee}
                  onChange={(e) => setSettingsForm({...settingsForm, reminder_fee: parseFloat(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('inkasso.interestRate')} (%)</Label>
                <Input 
                  type="number"
                  step="0.1"
                  value={settingsForm.interest_rate}
                  onChange={(e) => setSettingsForm({...settingsForm, interest_rate: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t('inkasso.autoSendReminders')}</Label>
                </div>
                <Switch 
                  checked={settingsForm.auto_send_reminders}
                  onCheckedChange={(checked) => setSettingsForm({...settingsForm, auto_send_reminders: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t('inkasso.autoSendToCollections')}</Label>
                </div>
                <Switch 
                  checked={settingsForm.auto_send_to_inkasso}
                  onCheckedChange={(checked) => setSettingsForm({...settingsForm, auto_send_to_inkasso: checked})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveSettings}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inkasso;
